import { useEffect, useState } from 'react'
import { Button } from 'vnetwork-ui'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { SelectField } from '@/components/ui/select'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getLocations, returnComponents } from '../../services'

interface ReturnToWarehouseDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    componentIds: string[]
    componentLabel: string
    onSuccess?: () => void
}

const ReturnToWarehouseDialog = ({
    open,
    onOpenChange,
    componentIds,
    componentLabel,
    onSuccess,
}: ReturnToWarehouseDialogProps) => {
    const [locationId, setLocationId] = useState('')

    const { data: locationsData } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.WAREHOUSE_LOCATIONS, 'warehouse-return-select'],
        queryFn: () => getLocations({ type: 'warehouse', page_size: 100 }),
    })

    const warehouses = locationsData?.results || []

    // Preselect when exactly one warehouse; reset when dialog closes
    useEffect(() => {
        if (!open) {
            setLocationId('')
        } else if (warehouses.length === 1) {
            setLocationId(warehouses[0].id)
        }
    }, [open, warehouses])

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: () =>
                returnComponents({
                    component_ids: componentIds,
                    location_id: locationId,
                }),
        },
        {
            successMessage: 'Components returned to warehouse',
            errorMessage: 'Failed to return components',
            reactQueryKeys: QUERY_KEYS.WAREHOUSE_ITEMS,
            // returnComponents resolves to void, so close on the success handler
            // rather than inspecting the mutation return value.
            onSuccess: () => {
                onOpenChange(false)
                onSuccess?.()
            },
        }
    )

    const handleConfirm = () => {
        if (!locationId) return
        mutateAsync()
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Đưa Về Kho</DialogTitle>
                    <DialogDescription>
                        Rút {componentIds.length} linh kiện ({componentLabel})
                        khỏi thiết bị và đưa về kho đã chọn.
                    </DialogDescription>
                </DialogHeader>

                <div className="py-2">
                    <label className="mb-2 block text-sm font-medium">
                        Kho nhận
                    </label>
                    <SelectField
                        value={locationId}
                        onValueChange={setLocationId}
                        options={warehouses.map((w) => ({
                            label: w.name,
                            value: w.id,
                        }))}
                        placeholder="Chọn kho..."
                        showClearButton={false}
                    />
                </div>

                <DialogFooter>
                    <Button
                        variant="outlined"
                        onClick={() => onOpenChange(false)}
                        disabled={isPending}
                    >
                        Huỷ
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={!locationId || isPending}
                    >
                        Đưa Về Kho
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}

export default ReturnToWarehouseDialog
